import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ENVIRONMENT } from 'shared/environment/environment';
import { LinkTarget } from './link.model';

@Component({
  selector: 'app-link',
  imports: [CommonModule, RouterModule],
  templateUrl: './link.component.html',
  styleUrl: './link.component.scss'
})
export class LinkComponent implements OnChanges {
  @Input() public url: string | undefined = '';
  @Input() public label = '';
  @Input() public classes = '';

  public linkTarget: LinkTarget = new LinkTarget({ kind: 'external', href: '' });

  public ngOnChanges(): void {
    let spaOrigins = ENVIRONMENT.spaOrigins || [];
    this.linkTarget = this.resolveLinkTarget(this.url, spaOrigins);
  }

  private resolveLinkTarget(input: string | undefined, spaOrigins: string[]): LinkTarget {
    let trimmed = (input ?? '').trim();

    if (!trimmed) {
      return new LinkTarget({ kind: 'external', href: '' });
    }

    let isAbsolute = /^https?:\/\//i.test(trimmed);

    // Wenn es sowieso schon relativ ist (z.B. "/content/page")
    if (!isAbsolute) {
      return this.parseInternalUrl(trimmed);
    }

    let url: URL;
    try {
      url = new URL(trimmed);
    } catch {
      // Fallback
      return { kind: 'external', href: trimmed };
    }

    // 1. Protokoll-unabhängige Prüfung:
    // Wir hängen intern 'https://' an den Origin an, falls er "nackt" (z.B. localhost:4200) aus dem Environment kommt.
    // So schluckt der URL-Parser die Eingabe fehlerfrei und wir vergleichen danach nur noch den ".host".
    let isConfiguredOrigin = spaOrigins.some(origin => {
      try {
        let parserUrl = origin.includes('://') ? origin : `https://${origin}`;
        return url.host === new URL(parserUrl).host;
      } catch {
        return false;
      }
    });

    // 2. Automatischer Check (SSR safe). Ebenfalls auf ".host" umgebaut, um HTTP/HTTPS Mismatches zu ignorieren.
    let isCurrentOrigin = typeof window !== 'undefined' && url.host === window.location.host;

    if (isConfiguredOrigin || isCurrentOrigin) {
      // Domain abschneiden, nur den Rest verwenden
      let internalPath = this.stripSpaBase(url.pathname, spaOrigins);
      return this.parseInternalUrl(internalPath + url.search + url.hash);
    }

    // Fremde Domain -> externer Link
    return { kind: 'external', href: trimmed };
  }

  private parseInternalUrl(rawPath: string): LinkTarget {
    let dummyUrl = new URL(rawPath, 'http://localhost');
    let path = dummyUrl.pathname; // Das ist z.B. "/de/category" oder "/de"

    // --- LANGUAGE-PREFIX STRIPPER ---
    if (typeof window !== 'undefined') {

      // Nutzt einen Regex Lookahead (?=\/|$).
      // Das bedeutet: Nach dem Kürzel MUSS ein Slash kommen ODER der String zu Ende sein.
      // Erkennt: "/de/", "/en-US/", "/de" (auch ohne trailing slash).
      let pathLangMatch = path.match(/^\/[a-zA-Z]{2,3}(-[a-zA-Z]{2})?(?=\/|$)/);

      if (pathLangMatch) {
        let pathLangPrefix = pathLangMatch[0]; // Erkennt z.B. "/de" oder "/de/"

        // Link ist exakt die Startseite der Sprache (z.B. "/de" oder "/de/")
        if (path === pathLangPrefix || path === pathLangPrefix + '/') {
          path = '/';
        } else {
          // Link beginnt mit Sprachkürzel -> Kürzel exakt abschneiden.
          // Aus "/en/kategorie" wird "kategorie" und anschließend zu "/kategorie"
          path = path.substring(pathLangPrefix.length);
          if (!path.startsWith('/')) {
            path = '/' + path;
          }
        }
      }
    }

    let queryParams: Record<string, string> = {};
    dummyUrl.searchParams.forEach((val, key) => {
      queryParams[key] = val;
    });

    let fragment = dummyUrl.hash.replace('#', '') || undefined;

    return {
      kind: 'internal',
      routerLink: path.startsWith('/') ? path : `/${path}`,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      fragment: fragment
    };
  }

  private stripSpaBase(pathname: string, spaOrigins: string[]): string {
    let bases = spaOrigins
      .map(x => {
        try {
          // Auch hier robust gegenüber "nackten" Domains machen
          let uStr = x.includes('://') ? x : `https://${x}`;
          let u = new URL(uStr);
          return u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
        } catch {
          return '';
        }
      })
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);

    let base = bases.find(b => pathname.startsWith(b + '/') || pathname === b);
    if (!base) return pathname;
    let rest = pathname.slice(base.length);
    return rest || '/';
  }
}
